/**
 * Deterministische Farbzuordnung pro User-ID.
 *
 * Joerg 2026-05-30: jeder Mitarbeiter soll seine eigene Farbe haben —
 * keine Kollisionen aus einer kleinen Palette.
 *
 * Joerg 2026-07-24 (Update): reine Hash-Ableitung fuehrte zu Kollisionen
 * (Kiara+Joerg identische Farbe). Neue Wahrheit ist die persistente
 * `addressbook.persons.color`-Spalte, aus Kelly-22-Palette beim Anlegen
 * vergeben. Der Hash-Weg bleibt als Fallback fuer Records, die aus
 * irgendeinem Grund noch keine Farbe haben (z.B. externe Ansprechpartner
 * ohne Login, oder Personen die vor Welle 7 angelegt wurden und noch
 * nicht durch den Backfill gelaufen sind).
 *
 * Aufrufer sollten zuerst die DB-Farbe abfragen und nur beim Fehlen
 * `colorForUserId` benutzen — oder direkt `colorForPerson({id, color})`,
 * das die Fallback-Logik intern hat.
 *
 * Falls userId null/leer ist, wird USER_COLOR_FALLBACK zurueckgegeben.
 */

export const USER_COLOR_FALLBACK = '#64748b'; // slate-500

/** FNV-1a 32-bit Hash — deterministisch. */
function fnv1a(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Liefert eine HSL-Farbe pro User-ID. Hue aus dem Hash (0..359),
 * Saettigung + Helligkeit fest gewaehlt fuer:
 *  - gute Sichtbarkeit auf hellem UND dunklem Hintergrund
 *  - Pin-tauglich auf der Leaflet-Karte (genug Kontrast zum Land-Background)
 *
 * Beispiel: derselbe MA → bei jedem Render dieselbe Farbe.
 */
export function colorForUserId(userId: string | null | undefined): string {
  if (!userId) return USER_COLOR_FALLBACK;
  const hash = fnv1a(userId);
  const hue = hash % 360;
  // Zusaetzlich Sat/Lig aus anderen Hash-Bytes variieren — reduziert
  // sichtbare Kollisionen auch bei diesem Fallback deutlich (2026-07-24).
  const sat = 60 + ((hash >>> 8) % 25);   // 60-84%
  const lig = 42 + ((hash >>> 16) % 12);  // 42-53%
  return `hsl(${hue}, ${sat}%, ${lig}%)`;
}

/**
 * Praktisches Wrapper — persistierte Farbe wenn vorhanden, sonst Hash-Fallback.
 * Aufrufer die Personen-Objekte aus der API bekommen (mit `color`-Feld) sollten
 * das hier nutzen und den Hash nur als Notnagel behalten.
 *
 * Beispiel:
 *   colorForPerson({ id: user.id, color: user.color })
 */
export function colorForPerson(
  p: { id: string | null | undefined; color?: string | null },
): string {
  if (p.color && p.color.trim()) return p.color;
  return colorForUserId(p.id);
}
